/**
 * Builds ProctoringClient config for FTS contributor-app.
 * Tenant + profile come from the Talent Studio monitor key payload (`slug` = fts).
 * API uses APAPI + Authal idToken.
 */

import type { ProctoringCapabilities } from '@telusinternationalai/proctoring-sdk'
import { getAuthTokens } from 'react-ai-platform-auth'
import { isSecureProctoringProfile } from 'Config/proctoring.config'
import { logProctoring } from './debugLog'
import type { ProctoringConfigInput, UseProctoringOptions } from './types'

export const capabilitiesForProfile = (profile?: string | null): ProctoringCapabilities => {
    const secure = isSecureProctoringProfile(profile)
    return {
        screen: true,
        video: !secure,
        audio: !secure,
        recording: true,
        flags: ['multi_monitor_detected'],
    }
}

const readProctoringApiToken = (): string => {
    const idToken = getAuthTokens()?.idToken
    if (!idToken) return ''
    return idToken.replaceAll(/^Bearer\s+/gi, '')
}

const readEnv = (value?: string | null) =>
    (value ?? '').trim().replaceAll(/^["']/g, '').replaceAll(/["']$/g, '')

export const buildProctoringConfig = (options: UseProctoringOptions): ProctoringConfigInput => {
    if (!options.referenceId) {
        throw new Error(
            'useProctoring: `referenceId` is required to initialize a proctoring session.'
        )
    }
    if (!options.tenant) {
        throw new Error('useProctoring: `tenant` is required to initialize a proctoring session.')
    }

    const baseUrl = readEnv(options.api?.baseUrl ?? process.env.ENV_APAPI_URL).replaceAll(
        /\/$/g,
        ''
    )
    if (!baseUrl) {
        throw new Error(
            'useProctoring: `ENV_APAPI_URL` (or options.api.baseUrl) is required for proctoring API calls.'
        )
    }

    const tenant = options.tenant.trim()
    const profile = (options.profile ?? 'default').trim()
    const token = options.api?.token ?? readProctoringApiToken()
    if (!token) {
        throw new Error(
            'useProctoring: Authal idToken is required for proctoring API calls (getAuthTokens().idToken).'
        )
    }

    logProctoring('config:built', {
        tenant,
        profile,
        referenceId: options.referenceId,
        apiBaseUrl: baseUrl,
        hasApiToken: true,
    })

    return {
        tenant,
        referenceId: options.referenceId,
        profile,
        expectedDurationSeconds: options.expectedDurationSeconds,
        capabilities: capabilitiesForProfile(profile),
        api: {
            baseUrl,
            token,
            requestTimeoutMs: options.api?.requestTimeoutMs,
        },
        options: options.options,
        observability: options.observability,
    }
}
